import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Provider as JotaiProvider } from "jotai";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./lib/i18n";
import { useMe } from "./data-provider";
import { ApiFetchError } from "./lib/api";
import { Spinner } from "./components/ui";

// Pages are lazy-loaded so the login bundle doesn't pull in the chat
// workspace, which has the message renderer and the dashboard chart
// code — a cold start that only visits Login shouldn't pay for them.
const Login = lazy(() => import("./pages/Login"));
const Chat = lazy(() => import("./pages/Chat"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

/** One QueryClient per app lifetime. Logging 401s as "unauthenticated"
 *  rather than retrying keeps the Login page from flickering while the
 *  session is still being hydrated. */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiFetchError && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    // Swallow 401s from `useMe()` — they're handled in the query hook.
    // Everything else bubbles up to the component's `error` state.
  }),
});

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center bg-surface-primary text-text-tertiary">
      <Spinner size={28} />
    </div>
  );
}

/** Route guard: wait for `/api/auth/me` to resolve, then route to Login
 *  or the authed workspace. Rendering Login early would flash the sign-in
 *  card during a session hydration, so we spin while the first fetch is
 *  in flight. */
function RouteGate() {
  const { data: user, isLoading } = useMe();

  if (isLoading) return <Loading />;

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/chat" replace />} />
      <Route path="/login" element={<Navigate to="/chat" replace />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/chat/:conversationId" element={<Chat />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <JotaiProvider>
          <Router>
            <Suspense fallback={<Loading />}>
              <RouteGate />
            </Suspense>
          </Router>
        </JotaiProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
